import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/lib/api';
import { RoleType, PacketStatus, type User, type PacketWithCandidate, FIRST_GRANULAR_YEAR } from '@promo/shared';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Spinner } from '@/components/ui/spinner';
import { EditableSelect, type SelectOption } from '@/components/ui/editable-select';
import { UserInfo } from '@/components/common/UserInfo';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { getDistinctPacketStatusLabel } from '@/lib/packetStatus';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@promo/shared';
import { DecisionBadge } from '@/components/ui/DecisionBadge';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useGlobalRoles } from '@/hooks/useGlobalRoles';
import { RatificationBadge } from '@/components/ui/RatificationBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { preparePacketDataForExport, convertToCSV, downloadCSV } from '@/lib/export';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';

const ALL_STATUS_VALUE = 'ALL';
const ALL_MANAGER_VALUE = 'ALL';

// Helper: get status options
const statusOptions: SelectOption[] = [
  { value: ALL_STATUS_VALUE, label: 'All' },
  ...Object.values(PacketStatus).map(s => ({
    value: s,
    label: getDistinctPacketStatusLabel(s),
  })),
];

// Helper: get year options from FIRST_GRANULAR_YEAR to current year
const generateYearOptions = (): SelectOption[] => {
  const currentYear = new Date().getFullYear();
  const years: SelectOption[] = [];
  
  for (let year = currentYear; year >= FIRST_GRANULAR_YEAR; year--) {
    years.push({
      value: year.toString(),
      label: year.toString(),
    });
  }
  
  return years;
};

const yearOptions = generateYearOptions();

export function getPacketDecisionColumnLabel(
  packet: Pick<PacketWithCandidate, 'ratification'>
): 'Decision' | 'Recommendation' {
  return packet.ratification ? 'Decision' : 'Recommendation';
}

export function getPacketDecisionColumnValue(
  packet: Pick<PacketWithCandidate, 'decision' | 'ratification' | 'recommendation'>
) {
  return packet.ratification ? packet.decision : (packet.recommendation ?? packet.decision);
}

export function PacketListPage() {
  const { session } = useAuth();
  const currentUser = session.user;

  // Fetch global roles from new endpoint
  const { data: globalRoles = [], isLoading: rolesLoading } = useGlobalRoles(currentUser?.id);

  // Detect global role
  const isManager = globalRoles.includes(RoleType.MANAGER);
  const isSrLeader = globalRoles.includes(RoleType.SENIOR_LEADER);
  const isHR = globalRoles.includes(RoleType.HR);

  // Initialize filter states based on role (memoized to prevent unnecessary re-renders)
  const defaultFilters = useMemo(() => {
    if (rolesLoading || !currentUser || globalRoles.length === 0) {
      return {
        managerId: '',
        status: '' as PacketStatus | '',
        needManagers: false
      };
    }

    if (isManager) {
      return {
        managerId: currentUser.id,
        status: '' as PacketStatus | '',
        needManagers: false
      };
    } else if (isSrLeader) {
      return {
        managerId: '',
        status: PacketStatus.SENIOR_LEADER_REVIEW,
        needManagers: true
      };
    } else if (isHR) {
      return {
        managerId: '',
        status: PacketStatus.HR_REVIEW,
        needManagers: true
      };
    }

    return {
      managerId: '',
      status: '' as PacketStatus | '',
      needManagers: false
    };
  }, [currentUser, globalRoles, isManager, isSrLeader, isHR, rolesLoading]);

  const [selectedManagerId, setSelectedManagerId] = useState<string>(defaultFilters.managerId);
  const [selectedStatus, setSelectedStatus] = useState<PacketStatus | ''>(defaultFilters.status);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Update filter states when defaults change (only once when role detection completes)
  React.useEffect(() => {
    if (!rolesLoading && globalRoles.length > 0) {
      setSelectedManagerId(defaultFilters.managerId);
      setSelectedStatus(defaultFilters.status);
    }
  }, [defaultFilters.managerId, defaultFilters.status, rolesLoading, globalRoles.length]);

  // Check if we need managers for the filter
  const needManagers = defaultFilters.needManagers;

  // Memoize query parameters to prevent unnecessary re-renders
  const queryParams = useMemo(() => ({
    year: selectedYear,
    ...(selectedManagerId ? { managerId: selectedManagerId } : {}),
    ...(selectedStatus ? { status: selectedStatus } : {}),
    ...(needManagers ? { include: 'managers' } : {}),
  }), [selectedYear, selectedManagerId, selectedStatus, needManagers]);

  // Fetch packets with optional managers in a single request
  const {
    data: packetsResponse,
    isLoading: isLoadingPackets,
    error: packetsError,
  } = useQuery<PacketWithCandidate[] | { packets: PacketWithCandidate[], managers: User[] }, Error>({
    queryKey: ['packets', queryParams],
    queryFn: () => get('/packets', queryParams),
    enabled: !!currentUser && !rolesLoading, // Wait for roles to be loaded
  });

  // Extract packets and managers from response
  const packets = Array.isArray(packetsResponse) 
    ? packetsResponse 
    : (packetsResponse as { packets: PacketWithCandidate[], managers: User[] })?.packets || [];
  
  const managers = useMemo(() => {
    return Array.isArray(packetsResponse) 
      ? [] 
      : (packetsResponse as { packets: PacketWithCandidate[], managers: User[] })?.managers || [];
  }, [packetsResponse]);

  const isLoadingManagers = isLoadingPackets;
  const managersError = packetsError;

  // Build manager options for dropdown
  const managerOptions: SelectOption[] = useMemo(
    () => [
      { value: ALL_MANAGER_VALUE, label: 'All' },
      ...managers.map(m => ({ value: m.id, label: m.name || m.email })),
    ],
    [managers]
  );

  // Filter UI visibility
  const showManagerFilter = isSrLeader || isHR;
  const showStatusFilter = isSrLeader || isHR;

  // Helper to get manager user for a packet
  const getManager = (managerId: string) => managers.find(m => m.id === managerId);

  return (
    <div className="container mx-auto p-4 md:p-8">
      <h1 className="text-2xl font-bold mb-8">Packets</h1>
      {/* Filters */}
      <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-muted-foreground mb-1">Assessment Year</label>
          <EditableSelect
            options={yearOptions}
            value={selectedYear}
            onValueChange={setSelectedYear}
            placeholder="Select year"
            className="w-full"
          />
        </div>
        {showManagerFilter && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Manager</label>
            {isLoadingManagers ? (
              <Spinner className="h-5 w-5" />
            ) : managersError ? (
              <div className="text-destructive">Failed to load managers</div>
            ) : (
              <EditableSelect
                options={managerOptions}
                value={selectedManagerId === '' ? ALL_MANAGER_VALUE : selectedManagerId}
                onValueChange={val => setSelectedManagerId(val === ALL_MANAGER_VALUE ? '' : val)}
                placeholder="Select manager"
                className="w-full"
              />
              // TODO: Support multi-select for managers in the future
            )}
          </div>
        )}
        {showStatusFilter && (
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Status</label>
            <EditableSelect
              options={statusOptions}
              value={selectedStatus === '' ? ALL_STATUS_VALUE : selectedStatus}
              onValueChange={val => setSelectedStatus(val === ALL_STATUS_VALUE ? '' : (val as PacketStatus))}
              placeholder="Select status"
              className="w-full"
            />
          </div>
        )}
      </div>
      {/* Export Button */}
      <div className="mb-8 flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const data = preparePacketDataForExport(packets, managers);
            const csvData = convertToCSV(data);
            const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
            const filename = `packets-export-${timestamp}.csv`;
            downloadCSV(csvData, filename);
          }}
          disabled={packets.length === 0}
        >
          <Download className="mr-2 h-4 w-4" />
          Export to CSV
        </Button>
      </div>
      {/* Packet List Table */}
      <div className="overflow-x-auto rounded-md bg-background">
        {isLoadingPackets ? (
          <div className="flex items-center gap-2 p-8"><Spinner className="h-5 w-5" /> Loading packets...</div>
        ) : packetsError ? (
          <div className="text-destructive p-8">Failed to load packets: {packetsError.message}</div>
        ) : packets.length === 0 ? (
          <div className="text-muted-foreground p-8">No packets found for the selected filters.</div>
        ) : (
          <div>
            {packets.map((packet, index) => {
              const isExpanded = expandedId === packet.id;
              const manager = getManager(packet.managerId);
              return (
                <React.Fragment key={packet.id}>
                  <div
                    className={`group border-b last:border-b-0 md:border-0 hover:bg-accent/40 transition-colors duration-100 cursor-pointer px-4 py-4 flex items-center ${index % 2 !== 0 ? 'bg-gray-100' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`View packet for ${packet.candidateName}`}
                    onClick={e => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      navigate(`/packets/${packet.id}`);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate(`/packets/${packet.id}`);
                      }
                    }}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
                      {/* Candidate */}
                      <div>
                        <div className="text-xs uppercase text-muted-foreground mb-1">Candidate</div>
                        <UserInfo
                          initials={packet.candidateInitials || ''}
                          name={packet.candidateName || ''}
                          currentTitle={packet.targetTitle || ''}
                        />
                      </div>
                      {/* Manager */}
                      <div>
                        <div className="text-xs uppercase text-muted-foreground mb-1">Manager</div>
                        {manager ? (
                          <UserInfo
                            initials={getInitials(manager.name, manager.email)}
                            name={manager.name || manager.email}
                            subtitle={"Manager"} // TODO: show actual role Manager or Sr Leader later
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                      {/* Status */}
                      <div>
                        <div className="text-xs uppercase text-muted-foreground mb-1">Status</div>
                        <StatusBadge status={packet.status} />
                      </div>
                      {/* Decision */}
                      <div>
                        <div className="text-xs uppercase text-muted-foreground mb-1">
                          {getPacketDecisionColumnLabel(packet)}
                        </div>
                        <DecisionBadge decision={getPacketDecisionColumnValue(packet)} className="capitalize" />
                      </div>
                    </div>
                    {/* Caret at far right */}
                    <div className="flex items-center justify-end md:pl-4 min-w-[40px]">
                      <button
                        className="p-2 rounded hover:bg-muted align-middle"
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        aria-expanded={isExpanded}
                        onClick={e => {
                          e.stopPropagation();
                          setExpandedId(isExpanded ? null : packet.id);
                        }}
                      >
                        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="bg-muted/10 px-4 py-4">
                      <div className="py-2 space-y-4">
                        {/* Summary */}
                        <div className="pb-6">
                          <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">Summary</div>
                          {packet.summary ? (
                            <MarkdownRenderer content={packet.summary} className="text-sm" />
                          ) : (
                            <div className="text-muted-foreground text-sm italic">No summary</div>
                          )}
                        </div>
                        {/* Ratification */}
                        <div className="pb-6">
                          <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">Ratification</div>
                          <RatificationBadge ratification={packet.ratification} />
                        </div>
                        {/* Justification */}
                        <div className="pb-6">
                          <div className="text-xs font-semibold text-muted-foreground mb-1 uppercase tracking-wide">Justification</div>
                          {(!packet.ratification) ? (
                            <Skeleton className="h-4 w-1/2" />
                          ) : packet.ratification === 'RATIFIED' ? (
                            <div className="text-muted-foreground text-sm italic">No justification needed</div>
                          ) : packet.ratification === 'OVERRULED' ? (
                            <MarkdownRenderer content={packet.decisionJustification || packet.overruleJustification || ''} className="text-sm" />
                          ) : (
                            <div className="text-muted-foreground text-sm italic">No justification</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
